import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { downloadFileOnWeb } from '../../utils/file.utils';

export type DocumentPreviewKind = 'pdf' | 'image' | 'unsupported';

const PDF_EXTENSIONS = ['pdf'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'];

export const getPreviewExtension = (name?: string | null): string => {
  if (!name) {
    return '';
  }
  // strip query string / hash of URLs and take the last path segment
  const lastSegment = name.split(/[?#]/)[0].split('/').pop() ?? '';
  const dotIndex = lastSegment.lastIndexOf('.');
  return dotIndex > 0 ? lastSegment.substring(dotIndex + 1).toLowerCase() : '';
};

export const getDocumentPreviewKind = (fileName: string, mimeType?: string | null): DocumentPreviewKind => {
  const extension = getPreviewExtension(fileName);
  if (mimeType === 'application/pdf' || PDF_EXTENSIONS.includes(extension)) {
    return 'pdf';
  }
  if ((mimeType && IMAGE_MIME_TYPES.includes(mimeType)) || IMAGE_EXTENSIONS.includes(extension)) {
    return 'image';
  }
  return 'unsupported';
};

/**
 * Full screen in-app preview of a document (PDF, JPG/PNG) with Download, Print and Close actions.
 * Other formats are offered for download only. Open it via DocumentPreviewService.
 */
@Component({
  selector: 'isophi-document-preview',
  templateUrl: './document-preview.component.html',
  styleUrls: ['./document-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // the styles also size the surrounding modal window, see the scss
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class DocumentPreviewComponent implements OnInit, OnDestroy {
  /** File name shown in the header and used for download. */
  fileName = '';
  /** Already loaded content (e.g. generated print template). */
  blob?: Blob;
  /** Remote document URL, fetched to a Blob so that it can be previewed and printed. */
  url?: string;
  mimeType?: string;

  kind: DocumentPreviewKind = 'unsupported';
  loading = false;
  /** URL shown in the preview: object URL of the blob, or the remote URL when fetching failed. */
  previewUrl?: string;

  @ViewChild('pdfFrame') pdfFrame?: ElementRef<HTMLIFrameElement>;

  protected readonly activeModal = inject(NgbActiveModal);
  private readonly cd = inject(ChangeDetectorRef);
  private objectUrl?: string;

  ngOnInit(): void {
    this.kind = getDocumentPreviewKind(this.fileName || this.url || '', this.mimeType || this.blob?.type);

    if (this.blob) {
      this.setBlob(this.blob);
      return;
    }

    if (this.url && this.kind !== 'unsupported') {
      this.loadRemoteFile(this.url);
    }
  }

  ngOnDestroy(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  get canPrint(): boolean {
    return this.kind !== 'unsupported' && !!this.previewUrl;
  }

  download(): void {
    if (this.blob) {
      downloadFileOnWeb(this.blob, this.fileName);
      return;
    }

    if (this.url) {
      // fetch the file so that the download keeps the file name; fallback to opening the URL (e.g. CORS)
      fetch(this.url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => downloadFileOnWeb(blob, this.fileName))
        .catch(() => window.open(this.url, '_blank'));
    }
  }

  print(): void {
    if (!this.previewUrl) {
      return;
    }

    if (this.kind === 'pdf') {
      this.printPdf();
    } else if (this.kind === 'image') {
      this.printImage(this.previewUrl);
    }
  }

  close(): void {
    this.activeModal.close();
  }

  private printPdf(): void {
    const frameWindow = this.pdfFrame?.nativeElement.contentWindow;
    try {
      if (!this.objectUrl || !frameWindow) {
        throw new Error('PDF frame is not printable');
      }
      frameWindow.focus();
      frameWindow.print();
    } catch {
      // cross-origin document or browser not allowing to print embedded PDF -> let user print it in a new tab
      window.open(this.previewUrl, '_blank');
    }
  }

  private printImage(imageUrl: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    const frameWindow = iframe.contentWindow;
    const frameDocument = iframe.contentDocument;
    if (!frameWindow || !frameDocument) {
      iframe.remove();
      window.open(imageUrl, '_blank');
      return;
    }

    const style = frameDocument.createElement('style');
    style.textContent = '@page { margin: 10mm; } body { margin: 0; text-align: center; } img { max-width: 100%; max-height: 100vh; }';
    frameDocument.head.appendChild(style);

    const image = frameDocument.createElement('img');
    image.onload = () => {
      frameWindow.focus();
      frameWindow.print();
      setTimeout(() => iframe.remove(), 1000);
    };
    image.onerror = () => {
      iframe.remove();
      window.open(imageUrl, '_blank');
    };
    image.src = imageUrl;
    frameDocument.body.appendChild(image);
  }

  private loadRemoteFile(url: string): void {
    this.loading = true;
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        // storages often return application/octet-stream -> set the type so that the browser can render it
        const type = this.kind === 'pdf' ? 'application/pdf' : blob.type || this.mimeType || '';
        this.blob = blob.type === type ? blob : new Blob([blob], { type });
        this.setBlob(this.blob);
      })
      .catch(() => {
        // e.g. CORS - show the remote file directly (printing then falls back to a new tab)
        this.previewUrl = url;
      })
      .finally(() => {
        this.loading = false;
        this.cd.markForCheck();
      });
  }

  private setBlob(blob: Blob): void {
    if (this.kind === 'pdf' && blob.type !== 'application/pdf') {
      blob = new Blob([blob], { type: 'application/pdf' });
      this.blob = blob;
    }
    this.objectUrl = URL.createObjectURL(blob);
    this.previewUrl = this.objectUrl;
    this.cd.markForCheck();
  }
}
